/**
 * Видео-референсы: типы ответов `/api/video-refs/*` и чистая логика экрана (спека §11.1, §12, §17).
 *
 * Типы списаны с Python (`videoref.detail`/`listing`/`limits`), не со спеки. Модуль без импортов —
 * гоняется под `node --test`. Сеть и react-query — в `videoRefApi.ts`.
 */

export type VideoRefStatus = "queued" | "fetching" | "splitting" | "ready" | "failed";
export type VideoRefSource = "link" | "upload" | "pin";
/** `null` — файл (площадки нет); `other` — ссылка с незнакомого хоста. */
export type Platform = "instagram" | "tiktok" | "pinterest" | "youtube" | "other";
export type SceneMark = "use" | "skip";
export type SceneOrigin = "auto" | "manual" | "merged" | "even";

/** Описание сцены композером (`video_ref_scene_desc_json`): поля — как вернула модель. */
export type SceneDesc = {
  framing?: string | null;
  camera?: string | null;
  action?: string | null;
  dress_focus?: string | null;
  transition?: string | null;
  on_screen_text?: string | null;
  [key: string]: unknown;
};

export type VideoRefScene = {
  id: number;
  /** Номер «сцена N» по порядку. */
  n: number;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  key_ms: number;
  key_path: string | null;
  origin: SceneOrigin | string;
  mark: SceneMark;
  score: number | null;
  note: string | null;
  desc: SceneDesc | null;
  /** Длительность будущего кадра, с (`videoref.shot_seconds`). */
  shot_s: number;
};

export type VideoRefSummary = { hook?: string; tempo?: string; works?: string; structure?: string };

export type VideoRefTask = { id: number; status: string; error: string | null; date_start: string | null };

/** `video_ref_split_json`: порог, склейки scdet, был ли сайдкар, сколько соседей склеено и сцен нарезано поровну. */
export type VideoRefSplit = {
  threshold: number;
  height?: number;
  sidecar: boolean;
  cuts: { ms: number; score: number }[];
  merged: number;
  even_split: number;
};

export type VideoRefLimits = {
  min_scenes: number;
  max_scenes: number;
  min_shot_s: number;
  max_shot_s: number;
  /** Потолок кадра по моделям ролика (`videoscenario.max_duration`), с: им сервер считает `shot_s` и гейт. */
  max_d?: number;
  /** [мин, макс] длительность ролика, мс. */
  duration_range_ms: [number, number];
  min_scene_ms: number;
  presets: { more: number; normal: number; fewer: number };
};

export type VideoRefProject = {
  id: number;
  name: string;
  date_create: string;
  scenario_applied: boolean;
  dress_id: number | null;
};

export type VideoRefInfo = {
  id: number;
  source: VideoRefSource;
  status: VideoRefStatus;
  platform: Platform | null;
  url: string | null;
  url_norm: string | null;
  pin_id: number | null;
  asset_path: string | null;
  title: string | null;
  uploader: string | null;
  uploader_url: string | null;
  caption: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  date_publish: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  note: string | null;
  summary: VideoRefSummary | null;
  split: VideoRefSplit | null;
  describe_cost: number | null;
  date_describe: string | null;
  describe_stale: boolean;
  error: string | null;
  task: VideoRefTask | null;
  author_name: string | null;
  date_create: string;
  date_ready: string | null;
};

/** GET /api/video-refs/{id} и тело большинства правок. */
export type VideoRefDetail = {
  ref: VideoRefInfo;
  scenes: VideoRefScene[];
  projects: VideoRefProject[];
  estimate: { describe: number; scenario: number };
  limits: VideoRefLimits;
};

/** Строка списка `GET /api/video-refs`. */
export type VideoRefRow = {
  id: number;
  source: VideoRefSource;
  status: VideoRefStatus;
  platform: Platform | null;
  url: string | null;
  title: string | null;
  uploader: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  /** Кадр первой сцены; у пина без сцен — превью пина. */
  poster_path: string | null;
  scenes: number;
  marked: number;
  projects: number;
  described: boolean;
  error: string | null;
  author_name: string | null;
  date_create: string;
  date_ready: string | null;
};

export type YtdlpInfo = { version: string | null; stale: boolean; error: string | null };

export type VideoRefList = { items: VideoRefRow[]; ytdlp: YtdlpInfo; limits: VideoRefLimits };

/** POST create / upload: 201 новый, 200 уже был (`existing`). */
export type CreatedRef = { ref: VideoRefDetail; existing: boolean };

export type DescribeResult = { ref: VideoRefDetail; cost: number | null; existing: boolean };

/** POST /project: ошибка композера приходит внутри 200 (`error`) — проект уже создан. */
export type ProjectResult = {
  project_id: number;
  existing: boolean;
  scenario: unknown;
  applied: boolean;
  cost: number | null;
  /** Ошибка композера: проект создан, сценария нет; повтор — с ТЕМ ЖЕ ключом. */
  error: { code: string; message: string } | null;
};

/**
 * Жизнь ключа идемпотентности платной записи: `true` — оставить (повтор уйдёт с тем же ключом).
 * Проект: 200 с `error` — проект уже создан, повтор находит его по ключу, новый ключ создал бы второй.
 * Описание: ключ снимается на любой успешный ответ.
 */
export function shouldKeepKey(kind: "describe" | "project", result: object | null | undefined): boolean {
  return kind === "project" && (result as { error?: unknown } | null | undefined)?.error != null;
}

/**
 * Окно «Создать проект»: что сделать с «замороженной» формой после исхода запроса (ДЕНЬГИ, §10.2).
 * `freeze` — проект есть или мог создаться: 200 с `error`, 409 (`BUSY` — первый запрос с этим ключом ещё
 * идёт), ≥ 500, обрыв сети или не-HTTP ошибка. Форма замирает на отправленном наборе: правка набора дала бы
 * новый ключ — второй проект и второй вызов композера. `release` — сценарий применён. `keep` — ничего не
 * менять: запроса не было, он в полёте (новый `mutate` стирает прежний исход) или отказ гейта (прочий 4xx —
 * проекта нет; уже замёрзшая форма так и остаётся). Разморозка ещё — только закрытием окна.
 */
export function projectFreezeStep(
  result: { applied?: boolean; error?: unknown; project_id?: number } | null | undefined,
  failure: unknown,
): "freeze" | "release" | "keep" {
  if (result) {
    if (result.applied && result.error == null) return "release";
    return result.project_id != null ? "freeze" : "keep";
  }
  if (failure == null) return "keep";
  const status = (failure as { status?: unknown }).status;
  if (typeof status !== "number") return "freeze";
  return status === 409 || status >= 500 || status < 400 ? "freeze" : "keep";
}

// ---------------------------------------------------------------------------
// подписи
// ---------------------------------------------------------------------------

export const STATUS_LABEL: Record<VideoRefStatus, string> = {
  queued: "в очереди",
  fetching: "скачивается",
  splitting: "разбирается",
  ready: "готов",
  failed: "ошибка",
};

const LIVE: readonly string[] = ["queued", "fetching", "splitting"];

/** Референс в работе: экран опрашивает каждые 3 с, переразбор — 409 `IN_PROGRESS`. */
export function inProgress(status: VideoRefStatus | string | null | undefined): boolean {
  return status != null && LIVE.includes(status);
}

const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  youtube: "YouTube",
  other: "другое",
};

export function platformLabel(code: Platform | string | null | undefined): string {
  return (code && PLATFORM_LABEL[code]) || "файл";
}

// ---------------------------------------------------------------------------
// время
// ---------------------------------------------------------------------------

/** `m:ss.d` — «0:03.2». Десятая — усечением: позиция плеера не должна «убегать» вперёд. */
export function fmtTime(ms: number): string {
  const tenths = Math.floor(Math.max(0, ms) / 100);
  const m = Math.floor(tenths / 600);
  const s = Math.floor((tenths % 600) / 10);
  return `${m}:${String(s).padStart(2, "0")}.${tenths % 10}`;
}

function comma(value: number): string {
  return value.toFixed(1).replace(".", ",");
}

/** «2,6 с» — запятая, один знак. */
export function fmtDur(ms: number): string {
  return `${comma(ms / 1000)} с`;
}

/** Секунды без хвоста нуля: 4 → «4», 14.5 → «14,5». */
export function fmtSec(s: number): string {
  return Number.isInteger(s) ? String(s) : comma(s);
}

// ---------------------------------------------------------------------------
// сцены и гейт «Создать проект»
// ---------------------------------------------------------------------------

const MIN_SHOT_S = 2;

/** Длительность кадра по сцене — как `videoref.shot_seconds`: к 0,5 с с половиной вверх, затем в [2, maxS]. */
export function shotSeconds(ms: number, maxS = 15): number {
  const rounded = (Math.floor((Math.round(ms) + 250) / 500) * 500) / 1000;
  return Math.min(Math.max(rounded, MIN_SHOT_S), maxS);
}

/** Что мешает «Создать проект» (§10.1, §17): пусто — можно. Считаются только сцены `use`. */
export function projectProblems(scenes: VideoRefScene[], limits: VideoRefLimits): string[] {
  const marked = scenes.filter((s) => s.mark === "use");
  const n = marked.length;
  if (n < limits.min_scenes) return [`отметьте хотя бы ${limits.min_scenes} сцены (сейчас ${n})`];
  if (n > limits.max_scenes) return [`не больше ${limits.max_scenes} сцен (сейчас ${n})`];
  const maxS = limits.max_d ?? limits.max_shot_s;
  const totalMs = marked.reduce((sum, s) => sum + shotSeconds(s.duration_ms, maxS) * 1000, 0);
  const [lo, hi] = limits.duration_range_ms;
  if (totalMs < lo || totalMs > hi) {
    return [`ролик получится ${fmtSec(totalMs / 1000)} с: нужно ${lo / 1000}–${hi / 1000} с`];
  }
  return [];
}

/** Лента ритма: полоски в процентах от длительности ролика; промежутки между сценами остаются дырами. */
export function ribbon(
  scenes: VideoRefScene[],
  totalMs: number,
): { id: number; left: number; width: number; mark: SceneMark }[] {
  if (!(totalMs > 0)) return [];
  return scenes.map((s) => ({
    id: s.id,
    left: (s.start_ms / totalMs) * 100,
    width: ((s.end_ms - s.start_ms) / totalMs) * 100,
    mark: s.mark,
  }));
}

/** Разделить можно, если позиция внутри сцены с запасом `minMs` с обеих сторон. */
export function canSplit(scene: VideoRefScene, atMs: number, minMs: number): boolean {
  return atMs - scene.start_ms >= minMs && scene.end_ms - atMs >= minMs;
}

function stamp(value: string): number {
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/** Описание сделано до правок сцен: есть сцена, созданная или изменённая позже `date_describe` (§8.7). */
export function describeStale(
  ref: { date_describe: string | null },
  scenes: { date_modify: string; date_create: string }[],
): boolean {
  if (!ref.date_describe) return false;
  const done = stamp(ref.date_describe);
  return scenes.some((s) => stamp(s.date_modify) > done || stamp(s.date_create) > done);
}

// ---------------------------------------------------------------------------
// карточка референса (§12)
// ---------------------------------------------------------------------------

/** «1 кадр», «3 кадра», «6 кадров». */
export function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return one;
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return few;
  return many;
}

/** Подпись ленты ритма: «2,1 · 1,4 · 3,0 с». */
export function rhythmLabel(scenes: { duration_ms: number }[]): string {
  return scenes.length ? `${scenes.map((s) => comma(s.duration_ms / 1000)).join(" · ")} с` : "";
}

/** «сцена 2 · 0:03.2–0:05.8 · 2,6 с → кадр 2,5 с»; стрелка — только если кадр выйдет другой длины. */
export function sceneTitle(scene: VideoRefScene): string {
  const head = `сцена ${scene.n} · ${fmtTime(scene.start_ms)}–${fmtTime(scene.end_ms)} · ${fmtDur(scene.duration_ms)}`;
  const differs = Math.abs(Math.round(scene.duration_ms / 100) / 10 - scene.shot_s) >= 0.05;
  return differs ? `${head} → кадр ${fmtSec(scene.shot_s)} с` : head;
}

/**
 * Бейджи сцены. Флагов «склеено»/«равномерно» в строке сцены нет — они выводятся из разбора:
 * авто-сцена без score не с начала ролика — кусок равномерной нарезки (§8.3); склейка scdet строго внутри
 * авто-сцены — соседи объединены (короткая, похожая или сверх потолка сцен).
 */
export function sceneBadges(scene: VideoRefScene, cuts: { ms: number }[] | null | undefined): string[] {
  if (scene.origin === "manual") return ["вручную"];
  const out: string[] = [];
  if ((cuts ?? []).some((c) => c.ms > scene.start_ms && c.ms < scene.end_ms)) out.push("склеено");
  if (scene.score === null && scene.start_ms > 0) out.push("равномерно");
  return out;
}

/** Позиция плеера внутри сцены — можно ставить опорный кадр (`start ≤ key < end`). */
export function insideScene(scene: { start_ms: number; end_ms: number }, ms: number): boolean {
  return ms >= scene.start_ms && ms < scene.end_ms;
}

/** Первая дыра не короче `minMs` (до первой сцены, между сценами, после последней) — умолчание «добавить сцену». */
export function firstGap(
  scenes: { start_ms: number; end_ms: number }[],
  totalMs: number,
  minMs: number,
): { start_ms: number; end_ms: number } | null {
  const sorted = [...scenes].sort((a, b) => a.start_ms - b.start_ms);
  let at = 0;
  for (const s of [...sorted, { start_ms: totalMs, end_ms: totalMs }]) {
    if (s.start_ms - at >= minMs) return { start_ms: at, end_ms: s.start_ms };
    at = Math.max(at, s.end_ms);
  }
  return null;
}

/** Секунды из поля («3,2», «3.2», «3») → мс; пусто или мусор — `null`. */
export function parseSeconds(text: string): number | null {
  const t = text.trim().replace(",", ".");
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(t)) return null;
  return Math.round(Number(t) * 1000);
}

export type Preset = "more" | "normal" | "fewer";

/** Пресет, которым сделан разбор; порог мимо пресетов или разбора нет — `null`. */
export function presetOf(threshold: number | null | undefined, presets: Record<Preset, number>): Preset | null {
  if (threshold == null) return null;
  return (Object.keys(presets) as Preset[]).find((k) => Math.abs(presets[k] - threshold) < 1e-6) ?? null;
}

/** Кадры будущего проекта — отмеченные сцены по порядку; длительность кадра — `shot_s` сервера. */
export function projectPlan(scenes: VideoRefScene[]): { count: number; seconds: number; durations: number[] } {
  const durations = scenes.filter((s) => s.mark === "use").map((s) => s.shot_s);
  return { count: durations.length, seconds: durations.reduce((a, b) => a + b, 0), durations };
}

/** Счётчик над сценами: «отмечено 6 из 9 · ролик 14,5 с». */
export function markedLabel(scenes: VideoRefScene[]): string {
  const plan = projectPlan(scenes);
  return `отмечено ${plan.count} из ${scenes.length} · ролик ${fmtSec(plan.seconds)} с`;
}

/** Сводка окна «Создать проект»: «6 кадров · 14,5 с · композер ≈ $0.10 · длительности: 2 · 2 · 3 с». */
export function planLabel(scenes: VideoRefScene[], scenarioEstimate: number): string {
  const p = projectPlan(scenes);
  return (
    `${p.count} ${plural(p.count, "кадр", "кадра", "кадров")} · ${fmtSec(p.seconds)} с · ` +
    `композер ≈ $${scenarioEstimate.toFixed(2)} · длительности: ${p.durations.map(fmtSec).join(" · ")} с`
  );
}

// ---------------------------------------------------------------------------
// комментарии: автосохранение очередью (`saveQueue.ts`)
// ---------------------------------------------------------------------------

/** Несохранённые комментарии: `ref` — общий, `scene:<id>` — сцены. Значение — текст как в поле. */
export type NoteEdits = Record<string, string>;

export const REF_NOTE = "ref";
export const sceneNoteKey = (id: number): string => `scene:${id}`;

/** Операции правки для `SaveQueue<…, NoteEdits>`: свежий текст поля вытесняет прежний. */
export const noteOps = {
  empty: (): NoteEdits => ({}),
  isEmpty: (e: NoteEdits): boolean => Object.keys(e).length === 0,
  merge: (a: NoteEdits, b: NoteEdits): NoteEdits => ({ ...a, ...b }),
};

/** После ответа на `sent`: снять то, что ушло и с тех пор не правилось; правленое во время запроса — остаётся. */
export function settleNotes(local: NoteEdits, sent: NoteEdits): NoteEdits {
  const out: NoteEdits = {};
  for (const [k, v] of Object.entries(local)) if (!(k in sent) || sent[k] !== v) out[k] = v;
  return out;
}

export type NoteTarget = { kind: "ref"; note: string | null } | { kind: "scene"; sceneId: number; note: string | null };

/**
 * Пакет комментариев — по целям (`noteTargets`), каждая сама по себе: удачная — `onOne(правка, ответ)` сразу
 * (в кэш и снять с поля); отказ, который сервер не примет никогда (`isReject`: 4xx — сцена удалена, длинно), —
 * `onOne(правка, null)`, соседи уходят дальше, а первый отказ — наружу в конце (очередь снимет пакет, `drain`
 * откажет). Прочая ошибка (сеть, 5xx) — стоп и наружу: пакет вернётся в очередь целиком (повтор уже
 * сохранённого — та же правка). → последний ответ.
 */
export async function sendNotes<T>(
  body: NoteEdits,
  send: (target: NoteTarget) => Promise<T>,
  isReject: (error: unknown) => boolean,
  onOne: (one: NoteEdits, result: T | null) => void,
): Promise<T> {
  let got: { value: T } | null = null;
  let rejected: { error: unknown } | null = null;
  for (const t of noteTargets(body)) {
    const key = t.kind === "ref" ? REF_NOTE : sceneNoteKey(t.sceneId);
    const one: NoteEdits = { [key]: body[key] as string };
    let result: T;
    try {
      result = await send(t);
    } catch (error) {
      if (!isReject(error)) throw error;
      rejected ??= { error };
      onOne(one, null);
      continue;
    }
    got = { value: result };
    onOne(one, result);
  }
  if (rejected) throw rejected.error;
  if (!got) throw new Error("нечего сохранять");
  return got.value;
}

/** Что отправить: общий первым, сцены следом; пустое поле — `null` (сервер сотрёт). */
export function noteTargets(e: NoteEdits): NoteTarget[] {
  const out: NoteTarget[] = [];
  for (const [k, v] of Object.entries(e)) {
    const note = v.trim() === "" ? null : v;
    if (k === REF_NOTE) out.unshift({ kind: "ref", note });
    else if (k.startsWith("scene:")) out.push({ kind: "scene", sceneId: Number(k.slice(6)), note });
  }
  return out;
}
